import { ErrorResponse } from "../models/apiResponse";
import { ProblemError, isProblemError } from "../models/problemError";

export type HttpErrorKind = "http" | "network" | "parse" | "config";

const MAX_RAW_LENGTH = 2048;

type HttpErrorInit = {
  kind: HttpErrorKind;
  message: string;
  url: string;
  status?: number;
  statusText?: string;
  code?: string;
  body?: unknown;
  raw?: string;
  cause?: unknown;
  correlationId?: string;
  problem?: ProblemError;
};

// A failed request. `status` is 0 for anything that never got an HTTP response.
export class HttpError extends Error {
  readonly isHttpError = true as const;
  readonly kind: HttpErrorKind;
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly code?: string;
  readonly body?: unknown;
  readonly raw?: string;
  readonly cause?: unknown;
  // The reference the payer is shown and support searches on (D-17). Present
  // on every failure, including the ones that never reached the server.
  readonly correlationId?: string;
  readonly problem?: ProblemError;

  constructor(init: HttpErrorInit) {
    super(init.message);
    this.name = "HttpError";
    this.kind = init.kind;
    this.status = init.status ?? 0;
    this.statusText = init.statusText ?? "";
    this.url = init.url;
    this.code = init.code;
    this.body = init.body;
    this.raw = init.raw;
    this.cause = init.cause;
    this.correlationId = init.correlationId;
    this.problem = init.problem;

    // Jest/babel may transpile below the tsconfig target, which breaks the
    // prototype chain for subclassed builtins.
    Object.setPrototypeOf(this, HttpError.prototype);
  }

  static network(
    url: string,
    cause: unknown,
    correlationId?: string
  ): HttpError {
    return new HttpError({
      kind: "network",
      message:
        cause instanceof Error && cause.message
          ? cause.message
          : "Network request failed",
      url,
      cause,
      correlationId,
    });
  }

  static parse(
    url: string,
    raw: string,
    correlationId?: string
  ): HttpError {
    return new HttpError({
      kind: "parse",
      message: "Invalid JSON in response body",
      url,
      raw: truncate(raw),
      correlationId,
    });
  }

  static config(
    url: string,
    message: string,
    correlationId?: string
  ): HttpError {
    return new HttpError({ kind: "config", message, url, correlationId });
  }
}

export const isHttpError = (error: unknown): error is HttpError =>
  typeof error === "object" &&
  error !== null &&
  (error as HttpError).isHttpError === true;

const truncate = (raw: string): string =>
  raw.length > MAX_RAW_LENGTH ? `${raw.slice(0, MAX_RAW_LENGTH)}…` : raw;

const isErrorEnvelope = (
  data: unknown
): data is Partial<ErrorResponse> & { raw: string } =>
  typeof data === "object" &&
  data !== null &&
  typeof (data as { raw?: unknown }).raw === "string";

// Reads the body once as text so a non-JSON error page (nginx 502, HTML
// redirect, empty body) cannot throw a SyntaxError out of the error path.
export async function toHttpError(
  response: Response,
  url: string,
  correlationId?: string
): Promise<HttpError> {
  const raw = await response.text().catch(() => "");

  let body: unknown;
  try {
    body = raw ? JSON.parse(raw) : undefined;
  } catch {
    body = undefined;
  }

  const base = {
    kind: "http" as const,
    url,
    status: response.status,
    statusText: response.statusText,
    body,
    raw: truncate(raw),
    correlationId,
  };

  // The SCHOOLFEE envelope. Without this branch it fell through to the bare
  // status line below, losing the code, both messages and the reference at
  // once — a payer saw "HTTP 409" and support had nothing to search.
  if (isProblemError(body)) {
    return new HttpError({
      ...base,
      message: body.message,
      code: body.code,
      correlationId: body.correlation_id || correlationId,
      problem: body,
    });
  }

  const statusLine = `HTTP ${response.status} ${
    response.statusText || ""
  }`.trim();

  if (isErrorEnvelope(body)) {
    return new HttpError({
      ...base,
      // Neither the developer message nor the raw body: the first is
      // server-side-only OPS context, and the second is the response
      // verbatim, truncated at 2048 characters rather than redacted. Both
      // used to reach a payer through `message`. The envelope stays on
      // `body` for a resolver that knows which field a payer may see.
      message: statusLine,
      code: body.mappedErrorCode,
    });
  }

  return new HttpError({
    ...base,
    message: statusLine,
  });
}
